import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useToggle } from 'react-use-v13';
import { cva } from 'class-variance-authority';

const containerVariants = cva('w-full flex flex-col gap-1 items-start', {
  variants: {
    clamp: {
      enabled: 'hover:bg-utility-white/10',
      disabled: '',
    },
  },
});

type ExpandablePreProps = {
  children: ReactNode;
  isForceExpanded?: boolean;
  /**
   * must be an integer, at least 1
   */
  lineClampValue: number;
};

export const ExpandablePre = ({
  children,
  isForceExpanded,
  lineClampValue,
}: ExpandablePreProps) => {
  const [isSelfExpanded, toggleIsSelfExpanded] = useToggle(false);
  const isExpanded = isForceExpanded || isSelfExpanded;
  const [isClamped, setIsClamped] = useState(false);

  const responseRef = useRef<HTMLPreElement | null>(null);

  const updateClamped = useCallback(() => {
    if (isExpanded || !responseRef.current) {
      return;
    }
    setIsClamped(responseRef.current.scrollHeight > responseRef.current.clientHeight);
  }, [isExpanded, setIsClamped]);

  useEffect(() => {
    updateClamped();

    window.addEventListener('resize', updateClamped);

    return () => {
      window.removeEventListener('resize', updateClamped);
    };
  }, [updateClamped]);

  return (
    <div className={containerVariants({ clamp: isClamped ? 'enabled' : 'disabled' })}>
      <pre
        className="max-w-full text-md text-neutral-700 font-sans text-pretty break-words whitespace-pre-wrap"
        style={
          isExpanded
            ? {}
            : {
                display: '-webkit-box',
                WebkitLineClamp: lineClampValue,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }
        }
        ref={node => {
          responseRef.current = node;
          updateClamped();
        }}
      >
        {children}
      </pre>
      {isClamped && !isForceExpanded && (
        <button
          className="cursor-pointer text-xs font-semibold text-primary-500 hover:underline"
          onClick={e => {
            if (isClamped) {
              toggleIsSelfExpanded();
            }
            e.stopPropagation();
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              if (isClamped) {
                toggleIsSelfExpanded();
              }
              e.stopPropagation();
            }
          }}
        >
          {isExpanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};
